import React, { useEffect, useId, useState } from 'react';
import { ArrowLeftRight, ChevronRight, CircleDashed, CircleDot, KeyRound, LucideIcon, Network, Orbit } from 'lucide-react';
import { Avatar, Pill, RELATION_TONE, initialsOf } from '../../../components/ui';
import { AuthImage } from '../../../components/media/AuthImage';
import { ROLE_DESCRIPTION, ROLE_LABEL, StructuralRole } from '../graphAnalytics';
import type { NetworkNode, RelationKind } from '../types';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

export const useReducedMotion = () => useMediaQuery('(prefers-reduced-motion: reduce)');

const ROLE_ICON: Record<StructuralRole, LucideIcon> = {
  hub: Orbit,
  broker: ArrowLeftRight,
  gatekeeper: KeyRound,
  connector: Network,
  peripheral: CircleDot,
  isolate: CircleDashed,
};

/** Structural role; gatekeepers are amber because they are a vulnerability */
export const RolePill: React.FC<{ role: StructuralRole; className?: string }> = ({ role, className = '' }) => {
  const Icon = ROLE_ICON[role];
  return (
    <Pill tone={role === 'gatekeeper' ? 'amber' : 'neutral'} title={ROLE_DESCRIPTION[role]} className={className}>
      <Icon className="h-3 w-3" strokeWidth={1.75} />
      {ROLE_LABEL[role]}
    </Pill>
  );
};

/** Icon-only role mark for dense lists */
export const RoleGlyph: React.FC<{ role: StructuralRole; className?: string }> = ({ role, className = '' }) => {
  const Icon = ROLE_ICON[role];
  return (
    <span
      title={`${ROLE_LABEL[role]} — ${ROLE_DESCRIPTION[role]}`}
      className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${role === 'gatekeeper' ? 'bg-amber-50 text-amber-600' : 'bg-neutral-100 text-neutral-500'} ${className}`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      <span className="sr-only">{ROLE_LABEL[role]}</span>
    </span>
  );
};

export const EntityAvatar: React.FC<{ node?: NetworkNode | null; size?: 'xs' | 'sm' | 'md' | 'lg'; photo?: boolean; className?: string }> = ({
  node,
  size = 'sm',
  photo = false,
  className = '',
}) => (
  <Avatar
    name={node?.label}
    kind={node?.type === 'organization' ? 'organization' : 'stakeholder'}
    size={size}
    className={className}
    image={
      photo && node?.imageId ? (
        <AuthImage
          mediaId={node.imageId}
          alt=""
          className="h-full w-full object-cover"
          fallback={initialsOf(node.label)}
        />
      ) : undefined
    }
  />
);

/** Thin percentile bar (0–100) */
export const PercentileBar: React.FC<{ value: number; className?: string; tone?: 'ink' | 'red' }> = ({ value, className = '', tone = 'ink' }) => (
  <span className={`block h-[3px] w-full overflow-hidden rounded-full bg-neutral-100 ${className}`} aria-hidden="true">
    <span
      className={`block h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none ${tone === 'red' ? 'bg-primary-600' : 'bg-neutral-800'}`}
      style={{ width: `${Math.max(2, Math.min(100, value))}%` }}
    />
  </span>
);

export const RailSection: React.FC<{
  title: React.ReactNode;
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  id?: string;
}> = ({ title, aside, className = '', children, id }) => {
  const autoId = useId();
  const headingId = id || autoId;
  return (
    <section className={`border-t border-neutral-200/70 px-4 py-3.5 first:border-t-0 ${className}`} aria-labelledby={headingId}>
      <div className="mb-2 flex min-h-[24px] items-center justify-between gap-2">
        <h3 id={headingId} className="text-[13px] font-normal tracking-[-0.005em] text-ink">
          {title}
        </h3>
        {aside && <div className="flex items-center gap-1 text-[12px] text-neutral-400">{aside}</div>}
      </div>
      {children}
    </section>
  );
};

/** Compact segmented tabs for narrow rails */
export function MiniTabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className = '',
}: {
  items: Array<{ value: T; label: React.ReactNode; title?: string; disabled?: boolean }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={`flex w-full items-center gap-0.5 rounded-full bg-neutral-100 p-0.5 ${className}`}>
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            title={item.title}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={`inline-flex h-7 min-w-0 flex-1 items-center justify-center gap-1 truncate whitespace-nowrap rounded-full px-2 text-[12px] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:opacity-40 ${
              active ? 'bg-white font-medium text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.04)]' : 'text-neutral-500 hover:text-ink'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/** Row for an entity in rails and dossiers: avatar, name, secondary line, trailing slot */
export const EntityRow: React.FC<{
  node?: NetworkNode | null;
  label?: string;
  sub?: React.ReactNode;
  lead?: React.ReactNode;
  trailing?: React.ReactNode;
  below?: React.ReactNode;
  onClick?: () => void;
  onHover?: (hovering: boolean) => void;
  active?: boolean;
  dim?: boolean;
  className?: string;
  size?: 'xs' | 'sm';
}> = ({ node, label, sub, lead, trailing, below, onClick, onHover, active, dim, className = '', size = 'xs' }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={() => onHover?.(true)}
    onMouseLeave={() => onHover?.(false)}
    onFocus={() => onHover?.(true)}
    onBlur={() => onHover?.(false)}
    className={`group flex w-full min-w-0 items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30 ${
      active ? 'bg-primary-50/60 hover:bg-primary-50' : ''
    } ${dim ? 'opacity-60' : ''} ${className}`}
  >
    {lead}
    <EntityAvatar node={node} size={size} />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] leading-tight text-ink">{label ?? node?.label ?? 'Unknown'}</span>
      {sub && <span className="mt-0.5 block truncate text-[11.5px] leading-tight text-neutral-500">{sub}</span>}
      {below}
    </span>
    {trailing}
  </button>
);

export const Chevron: React.FC = () => (
  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-300 transition-colors group-hover:text-neutral-500" strokeWidth={1.75} />
);

/** Line sample in a relationship-kind colour */
export const TieSwatch: React.FC<{ type: RelationKind; dashed?: boolean; className?: string }> = ({ type, dashed, className = '' }) => (
  <svg width="16" height="8" viewBox="0 0 16 8" className={`flex-shrink-0 ${className}`} aria-hidden="true">
    <line
      x1="1"
      y1="4"
      x2="15"
      y2="4"
      stroke={RELATION_TONE[type].hex}
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeDasharray={dashed ? '3 2.5' : undefined}
    />
  </svg>
);

export const NodeSwatch: React.FC<{ type: 'stakeholder' | 'organization'; className?: string }> = ({ type, className = '' }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" className={`flex-shrink-0 ${className}`} aria-hidden="true">
    {type === 'organization' ? (
      <rect x="1.5" y="1.5" width="9" height="9" rx="2.6" fill="#14b8a6" />
    ) : (
      <circle cx="6" cy="6" r="4.6" fill="#0ea5e9" />
    )}
  </svg>
);

/** Small stat cell for compact grids split by hairlines */
export const MiniStat: React.FC<{ label: string; value: React.ReactNode; hint?: string; className?: string }> = ({ label, value, hint, className = '' }) => (
  <div className={`min-w-0 px-3 py-2.5 ${className}`} title={hint}>
    <p className="truncate text-[11.5px] text-neutral-500">{label}</p>
    <p className="mt-1 truncate text-[17px] leading-none tracking-[-0.02em] tabular-nums text-ink">{value}</p>
  </div>
);
